import React, { Fragment, useCallback, useEffect } from 'react'

import { useDomainAPI } from '@/api/hook'
import { useDomainList, useDomainLoading } from '@/module/domain/hook'
import { CreateDomainInfoDto, DomainInfo } from '@/module/domain/types'
import { useSelected } from '@/module/home/hook'
import DomainList from './DomainList'
import DomainListHeader from './DomainListHeader'
import DomainAddModal, { ADD_DOMAIN_MODAL } from './DomainAddModal'
import { FallbackProps } from 'react-error-boundary'
import { useAuth } from '@/module/auth/hook'
import axios from 'axios'
import { StatusCodes } from 'http-status-codes'
import { initialUser } from '@/module/auth/context'
import { Navigate, useNavigate } from 'react-router-dom'

function DomainManagement() {
  const navigate = useNavigate()
  const { productType } = useSelected()
  const { query, postMutation } = useDomainAPI()
  const { setIsSaveLoading } = useDomainLoading()
  const { domain, setSelectedDomain, setDomainList, setDomainCount, setDomainUserCount } =
    useDomainList()

  useEffect(() => {
    if (query.data != null) {
      setDomainList(query.data)
    }
  }, [query.data])

  useEffect(() => {
    setDomainCount(domain.items.length)
    setDomainUserCount(domain.items.reduce((sum, { userCount }) => sum + (userCount ?? 0), 0))
  }, [domain.items])

  useEffect(() => {
    if (postMutation.isLoading) {
      setIsSaveLoading(true)
    }
  }, [postMutation.isLoading])

  const checkDuplicateDomain = useCallback(
    (currDomain: string) => {
      for (let i = 0; i < domain.items.length; i++) {
        if (domain.items[i].domain === currDomain) {
          return true
        }
      }
      return false
    },
    [domain.items],
  )

  const handleAddModal = useCallback((domain: DomainInfo) => {
    const dto: CreateDomainInfoDto = {
      domain: domain.domain,
      maxLicense: domain.maxLicense,
      description: domain.description,
      name: domain.name,
      locale: domain.locale,
      address: domain.address,
      adminCount: domain.adminCount,
      type: domain.type,
      createTime: Math.floor(new Date(Date.now()).getTime() / 1000),
      expireTime: Math.floor(new Date(domain.expireTime).getTime() / 1000),
    }

    postMutation.mutate(dto, {
      onSuccess: () => closeAddDomainModal(),
      onError: (err) => alert((err as Error).message),
      onSettled: () => setIsSaveLoading(false),
    })
  }, [])

  const openAddDomainModal = () => {
    ;(document.getElementById(ADD_DOMAIN_MODAL) as HTMLDialogElement)?.showModal()
  }
  const closeAddDomainModal = () => {
    ;(document.getElementById(ADD_DOMAIN_MODAL) as HTMLDialogElement)?.close()
  }

  return (
    <Fragment>
      <DomainListHeader
        domainCount={domain.count}
        domainUserCount={domain.userCount}
        onAddDomainClick={() => {
          setSelectedDomain(null)
          openAddDomainModal()
        }}
      />
      <DomainList
        list={domain.items}
        onClickListItem={(item) => {
          setSelectedDomain(item)
        }}
        onClickEditDomainPage={(item) => {
          navigate(`/${productType}/${encodeURIComponent(item.domain)}`)
        }}
      />
      <DomainAddModal
        onAdd={(domain) => {
          if (checkDuplicateDomain(domain.domain)) {
            alert('동일한 도메인이 존재합니다. ')
            return
          }

          handleAddModal(domain)
        }}
        onCancel={() => closeAddDomainModal()}
      />
    </Fragment>
  )
}

const nonEmpty = (value?: string | null): string | undefined =>
  value != null && value.length > 0 ? value : undefined

function describeError(error: unknown) {
  if (axios.isAxiosError(error)) {
    return {
      kind: 'AxiosError' as const,
      name: error.name,
      message:
        nonEmpty(error.message) ?? nonEmpty(error.response?.statusText) ?? 'Network request failed',
      status: error.response?.status,
      statusText: error.response?.statusText,
      code: error.code,
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      data: error.response?.data,
    }
  }

  if (error instanceof Error) {
    return {
      kind: 'Error' as const,
      name: nonEmpty(error.name) ?? 'Error',
      message: nonEmpty(error.message) ?? 'Unknown error',
      stack: error.stack,
    }
  }

  return {
    kind: 'Unknown' as const,
    name: 'UnknownError',
    message: typeof error === 'string' && error.length > 0 ? error : 'Unknown error occurred',
    raw: error,
  }
}

export function ErrorFallback({ error }: FallbackProps) {
  const { handleSetUser } = useAuth()
  const info = describeError(error)

  console.error('[ErrorFallback]', info, error)

  if (info.kind === 'AxiosError' && info.status === StatusCodes.UNAUTHORIZED) {
    handleSetUser(initialUser)
    return <Navigate to='/' replace />
  }

  return (
    <div className='flex h-full w-full flex-col gap-1 overflow-y-auto p-4'>
      <p className='font-semibold'>오류가 발생했습니다.</p>
      <p>{info.message}</p>
      {info.kind === 'AxiosError' && info.status != null && (
        <p className='text-sm text-gray-500'>
          [{info.status}] {info.method} {info.url}
        </p>
      )}
    </div>
  )
}

export default DomainManagement
